import { createSelector } from '@reduxjs/toolkit'

import { PRODUCT_NAME_MAP } from 'common/billing/constants'
import { BillingCurrency } from 'common/billing/constants/BillingCurrency'
import { ProductSlug } from 'common/billing/constants/ProductSlug'
import { AddOnSlug, AddOnStatus } from 'common/billing/entities/addOns/interfaces'
import { getBillingCurrency } from 'common/billing/entities/billingInfo/selectors'
import { ProductPrice, ProInfoProductFLEX } from 'common/billing/entities/pro/interfaces'
import { getProSettings } from 'common/billing/entities/pro/selectors'
import { getTotalMonthlyPrice } from 'common/billing/entities/product/selectors'

// This selector deliberately lives in its own file rather than
// `subscription/selectors/index.ts` — it needs `getProSettings` (pro/selectors) and
// `getBillingCurrency` (billingInfo/selectors), and both of those transitively import
// back into `subscription/selectors/index.ts` (via `miscellaneous/selectors`'s
// `getSubscriptionInfo` dependency). Adding that edge to the barrel file closes a real
// circular import (`subscription/selectors` -> `pro/selectors` ->
// `miscellaneous/selectors` -> `subscription/selectors`). Keeping it in a separate
// leaf module — imported directly by consumers, never re-exported from the barrel —
// avoids the cycle.
export interface SubscriptionDataPlan {
  title: string
  price: ProductPrice
  slug: ProductSlug | AddOnSlug
}

export interface SubscriptionData {
  total: {
    amount: number
    currency: BillingCurrency
  }
  plans: SubscriptionDataPlan[]
  addOns: SubscriptionDataPlan[]
  billingPeriod: Date
  nextPaymentDate: Date
}

export const getSubscriptionData = createSelector(
  getProSettings,
  getTotalMonthlyPrice,
  getBillingCurrency,
  (proSettings, totalAmount, currency): Nullable<SubscriptionData> => {
    const { products, addons_data, billing_period, period_ends } = proSettings || {}
    if (!products || !billing_period || !period_ends) {
      return null
    }

    return {
      total: {
        amount: totalAmount,
        currency,
      },
      plans: Object.values(products)
        .filter((plan: ProInfoProductFLEX) => plan.active)
        .map((plan: ProInfoProductFLEX) => ({
          title: PRODUCT_NAME_MAP[plan.type],
          price: plan.price,
          slug: plan.slug,
        })),
      addOns: addons_data
        .filter((addOn) => addOn.status === AddOnStatus.ACTIVE)
        .map((addOn) => ({
          title: addOn.name,
          price: addOn.price,
          slug: addOn.slug,
        })),
      billingPeriod: new Date(billing_period),
      nextPaymentDate: new Date(period_ends),
    }
  },
)
